/**
 * useNotifications Hook - Epic 47
 *
 * Manages notification state for MASTER users.
 * Includes polling for real-time updates.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getNotificacoes,
  getNotificacaoCount,
  getNotificacaoDetail,
  toggleNotificacaoLeitura,
  Notificacao,
} from '../services/notificationService';

export interface UseNotificationsReturn {
  // State
  notificacoes: Notificacao[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  selectedNotificacao: Notificacao | null;
  isDropdownOpen: boolean;

  // Actions
  fetchNotificacoes: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  viewNotificacao: (id: number) => Promise<void>;
  toggleLeitura: (id: number) => Promise<void>;
  setIsDropdownOpen: (open: boolean) => void;
  clearError: () => void;
}

const POLLING_INTERVAL = 30000; // 30 seconds

export const useNotifications = (isMaster: boolean): UseNotificationsReturn => {
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedNotificacao, setSelectedNotificacao] = useState<Notificacao | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const fetchNotificacoes = useCallback(async () => {
    if (!isMaster) return;

    try {
      setLoading(true);
      setError(null);
      const response = await getNotificacoes();
      setNotificacoes(response.notificacoes);
    } catch (err: any) {
      console.error('[Epic 47] Error fetching notifications:', err);
      setError('Erro ao carregar notificações');
    } finally {
      setLoading(false);
    }
  }, [isMaster]);

  const fetchUnreadCount = useCallback(async () => {
    if (!isMaster) return;

    try {
      const response = await getNotificacaoCount();
      setUnreadCount(response.count);
    } catch (err: any) {
      console.error('[Epic 47] Error fetching notification count:', err);
      // Silently fail for count - don't show error to user
    }
  }, [isMaster]);

  const viewNotificacao = useCallback(async (id: number) => {
    try {
      setLoading(true);
      const detail = await getNotificacaoDetail(id);
      setSelectedNotificacao(detail);

      // Update local state to reflect read status
      setNotificacoes(prev =>
        prev.map(n => n.id === id ? { ...n, lida: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      console.error('[Epic 47] Error viewing notification:', err);
      setError('Erro ao carregar detalhes da notificação');
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleLeitura = useCallback(async (id: number) => {
    try {
      const updated = await toggleNotificacaoLeitura(id);

      // Update local state
      setNotificacoes(prev =>
        prev.map(n => n.id === id ? { ...n, lida: updated.lida } : n)
      );

      // Update selected notification if it's the same
      if (selectedNotificacao?.id === id) {
        setSelectedNotificacao({ ...selectedNotificacao, lida: updated.lida });
      }

      // Update unread count
      setUnreadCount(prev => updated.lida ? Math.max(0, prev - 1) : prev + 1);
    } catch (err: any) {
      console.error('[Epic 47] Error toggling notification status:', err);
      setError('Erro ao atualizar status da notificação');
    }
  }, [selectedNotificacao]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Initial fetch and polling setup
  useEffect(() => {
    if (!isMaster) {
      setNotificacoes([]);
      setUnreadCount(0);
      return;
    }

    // Initial fetch
    fetchUnreadCount();

    // Set up polling
    pollingRef.current = setInterval(() => {
      fetchUnreadCount();
    }, POLLING_INTERVAL);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [isMaster, fetchUnreadCount]);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && isMaster) {
      fetchNotificacoes();
    }
  }, [isDropdownOpen, isMaster, fetchNotificacoes]);

  return {
    notificacoes,
    unreadCount,
    loading,
    error,
    selectedNotificacao,
    isDropdownOpen,
    fetchNotificacoes,
    fetchUnreadCount,
    viewNotificacao,
    toggleLeitura,
    setIsDropdownOpen,
    clearError,
  };
};

export default useNotifications;
